import { useEffect, useRef } from 'react';
import { useLocationStore } from '../store/useLocationStore';

/**
 * While `active`, auto-capture a device location the moment the browser
 * geolocation permission TRANSITIONS to 'granted' (the user just turned it on —
 * via the native prompt, the browser's site settings, etc.).
 *
 * Deliberately SCOPED: this only runs on the surfaces that are explicitly asking
 * the user to enable location (the TopBar locale switcher, the dish-card
 * distance gate, the first-run "Where are you?" prompt). Granting permission
 * anywhere else — another tab, OS settings while the user is just browsing the
 * feed — must NOT silently move their browse location. (Contrast the old
 * behavior, where DeviceLocationGuard auto-captured on any grant app-wide.)
 *
 * The capture runs through the caller's OWN flow (`capture`, typically
 * `useAddressCapture().handleUseDeviceLocation`) so the caller's `onCaptured`
 * side effects — closing the modal, redirecting off a locale-scoped page — fire
 * exactly as they do for the surface's own button.
 *
 * Only fires on a live transition while mounted+active (we attach the listener,
 * we don't capture on attach), and only when the current location isn't already
 * device-sourced. Degrades to a no-op where the Permissions API is unavailable
 * (some native webviews / older Safari) — the surface's button still works.
 */
export function useAutoCaptureOnPermissionGrant(active: boolean, capture: () => void) {
  // Stable ref so the listener always calls the latest capture fn without
  // re-subscribing the effect when the surface re-renders.
  const captureRef = useRef(capture);
  captureRef.current = capture;

  useEffect(() => {
    if (!active) return;
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return;

    let cancelled = false;
    let status: PermissionStatus | null = null;

    const onChange = () => {
      if (cancelled || status?.state !== 'granted') return;
      // Read the live source (not a stale closure) so a capture already in
      // flight — e.g. the surface's own button — isn't double-driven.
      if (useLocationStore.getState().locationSource === 'device') return;
      captureRef.current();
    };

    navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((result) => {
        if (cancelled) return;
        status = result;
        // Attach only — no capture on the initial read. A surface opening while
        // permission is already granted (but the source is a ZIP) leaves the
        // user on the explainer; they enable via the button. We act solely on
        // the off→on transition.
        result.onchange = onChange;
      })
      .catch(() => {
        /* Permissions API unsupported — nothing to listen to. */
      });

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, [active]);
}
